import React from 'react';
import { Modal, Table, Card, Row, Col, Statistic, Progress, Tag } from 'antd';
import { ProcessItemsResponse } from '../../services/api/dashboardService';
import './ProcessItemsModal.css';

interface ProcessItemsModalProps {
  visible: boolean;
  processItems: ProcessItemsResponse | null;
  onClose: () => void;
}

export const ProcessItemsModal: React.FC<ProcessItemsModalProps> = ({
  visible,
  processItems,
  onClose,
}) => {
  const columns = [
    {
      title: 'Item Name',
      dataIndex: 'item_name',
      key: 'item_name',
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: 'Current State',
      dataIndex: 'state_name',
      key: 'state_name',
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'State ID',
      dataIndex: 'current_state',
      key: 'current_state',
      render: (text: string) => <code>{text}</code>,
    },
    {
      title: 'Transition Date',
      dataIndex: 'transition_date',
      key: 'transition_date',
      render: (date: string) => <span>{date ? new Date(date).toLocaleString() : 'N/A'}</span>,
    },
    {
      title: 'Parameters',
      dataIndex: 'item_parameters',
      key: 'item_parameters',
      render: (params: Record<string, unknown>) => (
        <div className="parameters-display">
          {params && Object.keys(params).length > 0 ? (
            Object.entries(params).map(([key, value]) => (
              <div key={key} className="parameter-item">
                <span className="parameter-key">{key}:</span>
                <span className="parameter-value">{String(value)}</span>
              </div>
            ))
          ) : (
            <span className="no-parameters">No parameters</span>
          )}
        </div>
      ),
    },
  ];

  const getCapacityUtilizationColor = (utilization: number) => {
    if (utilization >= 90) return '#ff4d4f';
    if (utilization >= 70) return '#52c41a';
    return '#faad14';
  };

  return (
    <Modal
      title={processItems ? `${processItems.process_name} - Artifact Items` : 'Process Items'}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      className="process-items-modal"
    >
      {processItems && (
        <div className="process-items-content">
          {/* Process Summary */}
          <Card className="process-summary-card" size="small">
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="Total Items"
                  value={processItems.item_count}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Nominal Capacity"
                  value={processItems.capacity_metrics.nominal_capacity}
                  precision={1}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Actual Capacity"
                  value={processItems.capacity_metrics.actual_capacity}
                  precision={1}
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Actual Demand"
                  value={processItems.capacity_metrics.actual_demand}
                  precision={1}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
            </Row>

            <div className="capacity-utilization-section">
              <div className="utilization-header">
                <span>Capacity Utilization</span>
                <span className="utilization-percentage">
                  {processItems.capacity_metrics.utilization_percentage.toFixed(1)}%
                </span>
              </div>
              <Progress
                percent={processItems.capacity_metrics.utilization_percentage}
                status={
                  processItems.capacity_metrics.utilization_percentage >= 90
                    ? 'exception'
                    : processItems.capacity_metrics.utilization_percentage >= 70
                      ? 'active'
                      : 'normal'
                }
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': getCapacityUtilizationColor(
                    processItems.capacity_metrics.utilization_percentage
                  ),
                }}
              />
            </div>

            <div className="capacity-details">
              <Row gutter={16}>
                <Col span={8}>
                  <div className="capacity-detail-item">
                    <span className="detail-label">Planned Capacity:</span>
                    <span className="detail-value">
                      {processItems.capacity_metrics.planned_capacity.toFixed(1)}
                    </span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="capacity-detail-item">
                    <span className="detail-label">Target Demand:</span>
                    <span className="detail-value">
                      {processItems.capacity_metrics.target_demand.toFixed(1)}
                    </span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="capacity-detail-item">
                    <span className="detail-label">Demand vs Capacity:</span>
                    <span
                      className={`detail-value ${processItems.capacity_metrics.actual_demand > processItems.capacity_metrics.actual_capacity ? 'over-capacity' : 'under-capacity'}`}
                    >
                      {(
                        processItems.capacity_metrics.actual_demand -
                        processItems.capacity_metrics.actual_capacity
                      ).toFixed(1)}
                    </span>
                  </div>
                </Col>
              </Row>
            </div>
          </Card>

          {/* Items Table */}
          <Card title="Artifact Items" className="items-table-card">
            <Table
              columns={columns}
              dataSource={processItems.items || []}
              rowKey="item_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
              size="small"
              scroll={{ x: 800 }}
            />
          </Card>
        </div>
      )}
    </Modal>
  );
};
